/*************************/
/** GET Survey Info **/

import { HttpParams } from '@angular/common/http';
import { RouteParams } from '@app/helpers';
import { BroadcastFiltersData } from './broadcast.model';
import { OrganizationList, SeniorCenter } from './organization.model';
import { AccountFeatureLimitsData, DuplicateCommRecord } from './shared.model';
import { SelectorUserData, SelfCareGiverData } from './user.model';
import { PortalUserListItem } from './profile.model';
import { appValues } from '@app/shared';
import { GetSurveyConditionalLogicResponse } from './survey-question.model';
import { SafeUrl } from '@angular/platform-browser';

/************Survey*************/
export interface GetSurveyList {
    message: string;
    data: {
        count: number;
        rows: Survey[];
    };
    page: number;
    limit: number;
}

/* SURVEY RESPONSE */
export interface SurveyResponse {
    message: string;
    data: Survey;
}

/* SURVEY REQUEST */
export interface SurveyRequest {
    name: string;
    description: string;
    active: boolean;
    org_id: number;
    senior_center_id: number | null;
    existingSurveyId?: number;
}

/* SURVEY MODEL */
export interface Survey {
    id: number;
    name: string;
    description: string;
    active: boolean;
    senior_center_id: number;
    languages: string[];
    created_on: string;
    created_by: string;
    workflowExists : boolean,
    workflowId: number;
    executed?: boolean;
    finalSave?: boolean;
    finalSubmit?: boolean;
    percentage?: string;
}

/*Survey Form data */
export interface SurveyFormData {
    active: boolean;
    senior_center_id: number;
    senior_center?: SeniorCenter;
    name: string;
    languages: string[];
    description: string;
    executed?: boolean;
    existingSurveyId?: number;
    finalSave?: boolean;
    finalSubmit?: boolean;
}

/************Survey Questions*************/
export interface GetSurveyQuestionsList {
    message: string;
    data: {
        count: number;
        rows: SurveyQuestion[];
    };
    page: number;
    limit: number;
}

/* SURVEY QUESTION RESPONSE */
export interface SurveyQuestionResponse {
    message: string;
    data: SurveyQuestion;
}

/* SURVEY QUESTION REQUEST */
export interface SurveyQuestionRequest {
    survey_id: number;
    question_order: string;
    lang: string;
    survey_question: string;
    acceptable_answers: string;
    open_ended: boolean;
    orgId?: number;
}

/* SURVEY QUESTION MODEL */
export interface SurveyQuestion {
    id?: number;
    event_info?: string;
    question_order: number;
    lang: string;
    survey_question: string;
    acceptable_answers: string;
    open_ended: boolean;
    survey_id?: string;
    created_on?: string;
}

/* SURVEY QUESTION ORDER MODEL */
export interface SurveyQuestionOrderModel {
    id: number;
    order: number;
}

export interface SurveyQuestionOrderRequestModel {
    orderList: SurveyQuestionOrderModel[];
}

/************Survey History*************/
export interface SurveyHistory {
    runHistories?: any[];
    surveyname: string;
    instance_id: string;
    survey_id: string;
    run_date: string;
    organization: string;
    centers: string[];
    last_run_date: string;
    surveydeleted?: boolean;
    responseCount: string;
    original_run_by: string;
    last_run_by: string;
    member_ref?: string[];
    status: string;
    sent_to?: number;
    org_labels?: [{ id?: any, name: string, active: boolean }]
}
export interface GetSurveyHistory {
    message: string;
    data: {
        count: number;
        rows: SurveyHistory[];
    };
    page: number;
    limit: number;
}

/************Survey History Detail*************/
export interface SurveyHistoryDetail {
    runHistories?: SurveyInstanceHistoryDetail[];
    survey_id: string;
    surveyname: string;
    surveydescription: string;
    instance_id: string;
    run_date: string;
    organization: string;
    centers: string[];
    channels: string;
    eligiblecount: number;
    languages: string[];
    responses: number;
    sent_to: number;
    reSendEligibleCount: string;
    surveydeleted?: boolean;
    active?: boolean;
    organizationcode: string;
    duplicate_comm?: {
        broadcast_id?: DuplicateCommRecord[];
        survey_ids?: DuplicateCommRecord[];
    } | null;
    last_run_by: string;
    original_run_by: string;
    member_ref?: string[];
    last_run_date: string;
    status: string;
    scheduled_id?: number;
    org_label_ids?: any[];
}
export interface GetSurveyHistoryDetail {
    message: string;
    data: SurveyHistoryDetail[];
}

export interface SuveryDetailTable {
    title: string;
    subtitle?: string;
    tableContents: {
        name: string;
        value: string;
        status?: { class: string; value: string };
        isClickable?: string;
        url?: string | SafeUrl;
        fileType?: string;
        fileName?: string;
        fileSize?: number;
        attachmentUrl?: string;
        isHighlighted?: boolean;
    }[];
}

export interface SurveyInstanceHistoryDetail {
    instance_id: string;
    run_id: string;
    run_date: string;
    eligible_client_count: number;
    run_by: string[];
    responseCount: number;
    status: string;
}
export interface GetSurveyInstanceHistoryDetail {
    message: string;
    data: SurveyInstanceHistoryDetail[];
}

/************Survey Execution*************/
export interface SurveySendDetail {
    surveyname: string;
    surveydescription: string;
    organization: string;
    centers: string[];
    channels: string;
    eligiblecount: number;
    languages: string[];
    member_ref?: string[];
    last_run_by?: string;
    original_run_by?: string;
    created_by?: string;
    snap_summary: string;
    caregiver_summary: string;
    interest_summary: string;
    tags_summary?: string;
    schedule_time: string;
    duplicate_comm?: {
        broadcast_id?: DuplicateCommRecord[];
        survey_ids?: DuplicateCommRecord[];
    } | null;
    recipients?: SurveyResultRecipients[];
    org_label_ids?: any[];
    org_labels?: string;
    conversational_ai: boolean;
    advanceFilters?: {
        gender?: string[];
        race?: string[];
        languages?: string[];
        disability?: string[];
        veteran?: string[];
        zipcodes?: string[];
        age?: {
            min: number;
            max: number;
        };
        activationDate?: {
            from: string;
            to: string;
        };
    };
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: number[];
    };

}

export interface SurveySendResponse {
    message: string;
    data: SurveySendDetail[];
}

export interface SurveySendRequest {
    sendSurvey: boolean;
    input_scids: number[];
    channelSMS: boolean;
    channelVoice: boolean;
    conversational_ai : boolean;
    interest_ids?: string[];
    interestNames?: string[];
    programNames?: string[];
    labelNames?: string[];
    tagNames?: string[];
    snap_eligibility?: {
        value: boolean | null;
    };
    snap_enrollment?: {
        value: boolean | null;
    };
    snap_interest?: {
        value: boolean | null;
    };
    member_ref?: string[];
    self_caregiver?: {
        value: SelfCareGiverData | null;
    };
    schedule_time?: string;
    reruns?: string[];
    scheduled_survey_id?: number;
    preview_start?: {
        date: { rowNumber: string; commDate: string }[];
        offset: string;
        timezone_identifier?: string;
        timezone_name: string;
    };
    tags?: number[];
    sendResults?: boolean;
    orgAdminIds?: number[];
    org_label_ids?: number[];
    advanceFilters?: {
        gender?: string[];
        race?: string[];
        languages?: string[];
        disability?: string[];
        veteran?: string[];
        zipcodes?: string[];
        age?: {
            min: number;
            max: number;
        };
        activationDate?: {
            from: string;
            to: string;
        };
    };
    customFieldsFilters?: {
        fieldIds: number[];
        valueIds: (string | number)[];
    };
}

export class SurveyListResolverData {
    public organizationInfo?: OrganizationList;
    public selectedSeniorCenter?: SeniorCenter | null;
    public filterParams?: RouteParams;
    public surveyListData?: GetSurveyList;
    public isExecutionView: boolean;

    constructor(
        organizationInfo?: OrganizationList,
        selectedSeniorCenter?: SeniorCenter | null,
        filterParams?: RouteParams,
        surveyListData?: GetSurveyList,
        isExecutionView: boolean = false
    ) {
        this.organizationInfo = organizationInfo;
        this.selectedSeniorCenter = selectedSeniorCenter;
        this.filterParams = filterParams;
        this.surveyListData = surveyListData;
        this.isExecutionView = isExecutionView;
    }
}

export class SurveyDetailsResolverData {
    public organizationInfo?: OrganizationList;
    public selectedSeniorCenter?: SeniorCenter | null;
    public surveyFormData?: SurveyFormData;
    public surveyQuestions?: GetSurveyQuestionsList;
    public surveyQuestionsWithConditionalLogic?: GetSurveyConditionalLogicResponse;
    public isDuplicationForm: boolean;

    constructor(
        organizationInfo?: OrganizationList,
        selectedSeniorCenter?: SeniorCenter | null,
        surveyFormData?: SurveyFormData,
        surveyQuestions?: GetSurveyQuestionsList,
        surveyQuestionsWithConditionalLogic?: GetSurveyConditionalLogicResponse,
        isDuplicationForm: boolean = false
    ) {
        this.organizationInfo = organizationInfo;
        this.selectedSeniorCenter = selectedSeniorCenter;
        this.surveyFormData = surveyFormData;
        this.surveyQuestions = surveyQuestions;
        this.surveyQuestionsWithConditionalLogic = surveyQuestionsWithConditionalLogic;
        this.isDuplicationForm = isDuplicationForm;
    }
}

/************Scheduled Survey*************/

/* SURVEY Scheduled MODEL */
export interface SurveyScheduled {
    id: number;
    survey_id: number;
    input_orgid: number;
    input_scids: string;
    channelSMS: boolean;
    channelVoice: boolean;
    conversational_ai : boolean;
    interest_id: number[];
    run_by: string;
    schedule_time: string;
    status: string;
    createdAt: string;
    parameters: {
        snap_eligibility?: boolean;
        snap_enrollment?: boolean;
        snap_interest?: boolean;
        self_caregiver?: SelfCareGiverData;
        member_ref?: string[];
        ph_interested?: boolean;
        ph_consented?: boolean;
        ph_info_provided?: boolean;
        advanceFilters?: {
            gender?: string[];
            race?: string[];
            languages?: string[];
            disability?: string[];
            veteran?: string[];
            zipcodes?: string[];
            age?: {
                min: number;
                max: number;
            };
            activationDate?: {
                from: string;
                to: string;
            };
        };
        customFieldsFilters?: {
        fieldIds: number[];
        valueIds: number[];
    };
    };
    Reruns: SurveyScheduledRerun[];
    Survey: {
        id: string;
        description: string;
    };
    member_details?: {
        member_ref: string;
        fname: string;
        lname: string;
        org_id: number;
        ClientCenters: {
            senior_center_id: number;
            SeniorCenter: { name: string };
        }[];
    }[];
    eligible_count?: string;
    tags?: number[];
    sendResults?: boolean;
    recipients?: SurveyResultRecipients[];
    org_label_ids?: any[];
}

/* SURVEY Scheduled RERUN */
export interface SurveyScheduledRerun {
    id: string;
    schedule_time: string;
    createdAt: string;
    status: string;
    run_by: string;
}

/* SURVEY Scheduled REQUEST */
export interface SurveyScheduledRequest {
    orgId: number;
    input_scids?: number[];
    channelSMS: boolean;
    channelVoice: boolean;
    snap_eligibility?: {
        value: boolean | null;
    };
    snap_enrollment?: {
        value: boolean | null;
    };
    snap_interest?: {
        value: boolean | null;
    };
    interest_ids?: number[];
    schedule_time?: string;
    self_caregiver?: { value: SelfCareGiverData };
    reruns: string[];
    deleted_reruns?: number[];
    updated_reruns?: { id: number; schedule_time: string }[];
}

/** SURVEY FORM RESOLVER DATA */
export class SurveyFormResolverData {
    organizationData: OrganizationList;
    selectedSeniorCenter: SeniorCenter | null = null;
    surveyId: number;
    surveyData: Survey;
    scheduleId: number | null = null;
    scheduledData: SurveyScheduled | null = null;
    filterData: BroadcastFiltersData | null = null;
    hasNoEligible?: boolean;
    featureLimitData?: AccountFeatureLimitsData;

    constructor(
        orgData: OrganizationList,
        center: SeniorCenter | null,
        surveyId: number,
        surveyData: Survey,
        scheduleId: number | null = null,
        scheduledData: SurveyScheduled | null = null,
        filterData: BroadcastFiltersData | null = null
    ) {
        this.organizationData = orgData;
        this.selectedSeniorCenter = center;
        this.surveyId = surveyId;
        this.scheduleId = scheduleId;
        this.surveyData = surveyData;
        this.scheduledData = scheduledData;
        this.filterData = filterData;
    }
}

export interface SurveyPreviewResponse {
    payload: SurveySendRequest;
    response: SurveySendDetail;
}

export interface SuggestMessageResponse {
    suggested_message: string;
}

export interface SurveySentUser {
    member_ref: string;
    fname: string;
    lname: string;
    lang: string;
    instance_id: string;
    channel: string;
    comms: string;
    delivery_status: string | { class: string; value: string };
    final_status: string | { class: string; value: string };
    timestamp: string;
}

export interface SurveyResponseLog {
    member_ref: string;
    org_name: string;
    program_name: string;
    fname: string;
    lname: string;
    survey_id: number;
    survey_name: string;
    response_date: string;
    response_channel: string;
    partial_response: string;
    questions: { question: string; answer: string }[];
}

export interface SurveyResponseChartsData {
    channels: {
        sms: number;
        voice: number;
        columns: string[][];
        options: { [k: string]: any };
    };
    responseDates: {
        columns: string[][];
        dataRows: string | number[][];
        options: { [k: string]: any };
    };
    programs: {
        columns: string[][];
        dataRows: string | number[][];
        options: { [k: string]: any };
    };
    partialResponse: {
        columns: string[][];
        yes: number;
        no: number;
        options: { [k: string]: any };
    };
    responses: SurveyResponseLog[];
}

export interface SurveyResultRecipients {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    address: string;
    userEmail: string;
    organisationId: number;
    id: number;
    is_logged_in_user?: boolean;
}

export interface SurveyQuestionResponses {
    message: string;
    totalQuestions: number;
    combinedData: {
        question_order: number;
        survey_question: string;
        responses: { [k: string]: number };
    }[];
}

export interface SurveyRerunCountsData {
    rerunsCount: number;
    instanceId: string;
}

export class SurveyDashboardViews {
    responsesChart: boolean = false;
    responseDatesChart: boolean = false;
    questionsChart: boolean = false;
}
export class SurveyDashboardData {
    numOfQuestions?: number;
    numOfPrograms?: number;
    numOfReruns?: number;
    numOfReceivers?: number;
    numOfResponded?: number;
    reponsePercentage?: any;
    responseDates?: SurveyDashboardDateResponseCounts[];
    questionResponse?: SurveyDashboardQuestionResponse[];
}

export interface SurveyDashboardDateResponseCounts {
    date: string;
    numOfUserResponses: number;
}
export interface SurveyDashboardQuestionResponse {
    order: number;
    question: string;
    options: {
        name: string;
        numOfUserResponse: number;
        responsePercentage: number;
    }[];
}

export interface UpdatedSurveyDashboardQuestionResponse {
    data: SurveyDashboardQuestionResponse[];
    totalQuestions: number;
}

export type SurveyEditType = appValues.surveyForm | appValues.surveyQuestion;

export interface GetUpcomingScheduledSurveyResponse {
    is_scheduled_survey: boolean;
}
